import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { buildRoute } from '@/shared/lib/constants';
import type { MorningSummaryAlert } from '../types';
import { getAlertTypeLabel } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface RecentAlertsCardProps {
  farmId: string;
  alerts: MorningSummaryAlert[];
}

const ITEMS_TO_SHOW = 5;

export const RecentAlertsCard: React.FC<RecentAlertsCardProps> = ({ farmId, alerts }) => {
  const [showAll, setShowAll] = useState(false);

  const visibleAlerts = showAll ? alerts : alerts.slice(0, ITEMS_TO_SHOW);
  const hasMore = alerts.length > ITEMS_TO_SHOW;

  // Get route based on source record type
  const getAlertRoute = (alert: MorningSummaryAlert): string => {
    switch (alert.sourceRecordType) {
      case 'water_quality_test':
        return buildRoute.waterQualityDetail(farmId, alert.pondId, alert.sourceRecordId);
      case 'fish_health_check':
        return `/farms/${farmId}/fish-health/${alert.sourceRecordId}`;
      case 'maintenance_check':
        return `/farms/${farmId}/maintenance/${alert.sourceRecordId}`;
      default:
        return `/farms/${farmId}/ponds/${alert.pondId}`;
    }
  };

  // Get icon color based on alert type
  const getAlertTypeColor = (alertType: string): string => {
    switch (alertType) {
      case 'water_quality':
        return 'bg-blue-100 text-blue-600';
      case 'parasite':
        return 'bg-purple-100 text-purple-600';
      case 'equipment':
        return 'bg-amber-100 text-amber-600';
      case 'environmental':
        return 'bg-green-100 text-green-600';
      case 'mortality':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-red-500" />
          <CardTitle>Recent Alerts (Last 24h)</CardTitle>
          <span className="ml-auto text-sm text-gray-500">{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {visibleAlerts.map((alert) => (
            <Link
              key={alert.alertId}
              to={getAlertRoute(alert)}
              className="flex items-start gap-3 p-3 rounded-lg border border-slate-200/50 hover:bg-slate-50 transition-colors"
            >
              {/* Severity indicator */}
              <div className="flex flex-col items-center gap-1 pt-0.5">
                <div className={`w-2 h-2 rounded-full ${
                  alert.severity === 'critical' ? 'bg-red-500' :
                  alert.severity === 'high' ? 'bg-amber-500' :
                  'bg-blue-500'
                }`} />
              </div>

              {/* Alert content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${getAlertTypeColor(alert.alertType)}`}>
                    {getAlertTypeLabel(alert.alertType)}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{alert.pondName}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{alert.message}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatDistanceToNow(new Date(alert.occurredAt), { addSuffix: true })}
                </p>
              </div>

              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
            </Link>
          ))}

          {/* Show more/less toggle */}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium mt-2"
            >
              {showAll ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  View all alerts ({alerts.length - ITEMS_TO_SHOW} more)
                </>
              )}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
